/**
 * Inline voice recorder for the client mobile PWA.
 * Mic → record → stop → preview → send/discard.
 * Uses MediaRecorder (audio/webm or audio/mp4 depending on platform).
 */

import { useEffect, useRef, useState } from 'react';
import { Mic, Square, Play, Pause, Trash2, Send, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { uploadFile, sendMessage as sendClientMessage } from '@/services/api/client-auth';
import { toast } from 'sonner';

type Phase = 'idle' | 'recording' | 'review' | 'sending';

function fmtTime(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function pickMime(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

export function VoiceRecorder({ onSent, onCancel }: { onSent?: () => void; onCancel?: () => void }) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef<number | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (tickRef.current) cancelAnimationFrame(tickRef.current);
      if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    };
  }, []);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        blobRef.current = blob;
        if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = URL.createObjectURL(blob);
        setPhase('review');
        stream.getTracks().forEach(t => t.stop());
      };
      rec.start();
      recRef.current = rec;
      startedAtRef.current = performance.now();
      setElapsed(0);
      setPhase('recording');
      const tick = () => {
        setElapsed(performance.now() - startedAtRef.current);
        tickRef.current = requestAnimationFrame(tick);
      };
      tickRef.current = requestAnimationFrame(tick);
    } catch (e: any) {
      toast.error(e?.message || 'Microphone access denied');
    }
  }

  function stop() {
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    if (tickRef.current) cancelAnimationFrame(tickRef.current);
  }

  function discard() {
    blobRef.current = null;
    if (audioUrlRef.current) { URL.revokeObjectURL(audioUrlRef.current); audioUrlRef.current = null; }
    setPhase('idle');
    setElapsed(0);
    onCancel?.();
  }

  async function send() {
    if (!blobRef.current) return;
    setPhase('sending');
    try {
      const ext = (blobRef.current.type.includes('mp4') ? 'm4a' : 'webm');
      const file = new File([blobRef.current], `voice-note-${Date.now()}.${ext}`, { type: blobRef.current.type });
      const uploaded = await uploadFile(file);
      await sendClientMessage('', uploaded.id);
      toast.success('Voice note sent');
      blobRef.current = null;
      if (audioUrlRef.current) { URL.revokeObjectURL(audioUrlRef.current); audioUrlRef.current = null; }
      setPhase('idle');
      setElapsed(0);
      onSent?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || 'Failed to send');
      setPhase('review');
    }
  }

  function togglePlay() {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); }
    else { a.pause(); setPlaying(false); }
  }

  if (phase === 'idle') {
    return (
      <button
        type="button"
        onClick={start}
        className="flex items-center justify-center gap-2 rounded-md border border-border bg-card px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted"
        aria-label="Record voice message"
      >
        <Mic className="h-4 w-4" />
      </button>
    );
  }

  if (phase === 'recording') {
    return (
      <div className="flex items-center gap-2 rounded-md border border-rose-300 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-3 py-2.5">
        <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
        <span className="text-sm font-mono tabular-nums text-rose-800 dark:text-rose-200 flex-1">{fmtTime(elapsed)}</span>
        <button type="button" onClick={stop} className="flex h-7 w-7 items-center justify-center rounded-md bg-rose-600 text-white" aria-label="Stop recording">
          <Square className="h-3 w-3 fill-current" />
        </button>
      </div>
    );
  }

  // review / sending
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
      <audio ref={audioRef} src={audioUrlRef.current || undefined} onEnded={() => setPlaying(false)} className="hidden" />
      <button type="button" onClick={togglePlay} disabled={phase === 'sending'} className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--v4-accent)] text-white disabled:opacity-50">
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ms-0.5" />}
      </button>
      <span className="text-sm font-mono tabular-nums text-muted-foreground flex-1">{fmtTime(elapsed)}</span>
      <button type="button" onClick={discard} disabled={phase === 'sending'} className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-rose-600 disabled:opacity-50" aria-label="Discard">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
      <button type="button" onClick={send} disabled={phase === 'sending'} className={cn('flex items-center gap-1.5 rounded-md bg-[var(--v4-accent)] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50')}>
        {phase === 'sending' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        Send
      </button>
    </div>
  );
}
